import { Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper, TextField, Tooltip, Typography, makeStyles, useTheme } from '@material-ui/core'
import { Dashboard, Favorite, GetApp, OpenInNew, Star, Store, ViewList } from '@material-ui/icons'
import { IBoard, MessageCard } from '@vestaboard/installables'
import { TagReviewItem, TagReviewStatus } from './types'

import { Autocomplete } from '@material-ui/lab'
import { HISTORY } from '../router'
import React from 'react'
import {
  useApproveTagReviewItem,
  useRejectTagReviewItem,
  useRetryTagReviewItem,
  useRevertTagReviewItem,
  useUnRejectTagReviewItem,
  useUpdateTagReviewItemTags
} from './hooks/useTagReviewMutations'

const LEFT_COLUMN_WIDTH = 380
const CARD_MIN_HEIGHT = 280

const truncateId = (id: string) => id.split('-')[0]

const getEntityIdLabel = (item: TagReviewItem): string => {
  switch (item.entityType) {
    case 'MessageSetMessage':
      return 'Message ID'
    case 'MarketplaceListing':
      return 'Listing ID'
    case 'Pick':
      return 'Pick ID'
    case 'Template':
      return 'Template ID'
  }
}

const getEntityPath = (item: TagReviewItem): string => {
  switch (item.entityType) {
    case 'MessageSetMessage':
      return `/message/${item.entityId}`
    case 'MarketplaceListing':
      return `/marketplace-listings/${item.entityId}`
    case 'Pick':
      return `/picks/${item.entityId}`
    case 'Template':
      return `/message-templates/message/${item.entityId}`
  }
}

const getEntityIcon = (type: TagReviewItem['entityType']): React.ReactElement => {
  switch (type) {
    case 'MessageSetMessage':
      return <ViewList fontSize='small' />
    case 'MarketplaceListing':
      return <Store fontSize='small' />
    case 'Pick':
      return <Star fontSize='small' />
    case 'Template':
      return <Dashboard fontSize='small' />
  }
}

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'row' as const,
    overflow: 'hidden',
    minHeight: CARD_MIN_HEIGHT
  },
  leftColumn: {
    width: LEFT_COLUMN_WIDTH,
    minWidth: LEFT_COLUMN_WIDTH,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    position: 'relative' as const
  },
  leftColumnMessageCard: {
    padding: 0
  },
  leftColumnMpl: {
    padding: 0
  },
  mplContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(8),
    textAlign: 'center' as const
  },
  rightColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const
  },
  controlsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 3)
  },
  controlsBody: {
    flex: 1,
    padding: theme.spacing(2, 3),
    display: 'flex',
    flexDirection: 'column' as const
  },
  controlsFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: theme.spacing(1.5, 3)
  },
  tagAutocomplete: {
    '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
      maxHeight: 140,
      overflowY: 'auto',
      alignItems: 'flex-start',
      flexWrap: 'wrap'
    }
  },
  mplRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  mplIcon: {
    width: 120,
    height: 120,
    borderRadius: 16,
    flexShrink: 0
  },
  footerWrapper: {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    backgroundColor: '#ffffff',
    borderTop: `1px solid ${theme.palette.grey[500]}`,
    padding: theme.spacing(1.5, 3)
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  popularityChip: {
    height: 26,
    fontSize: '0.75rem',
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5)
  },
  categoryChip: {
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 3),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  placeholder: {
    color: theme.palette.text.disabled
  },
  entityLink: {
    cursor: 'pointer',
    fontWeight: 500,
    borderRadius: 4,
    padding: '2px 6px',
    margin: '-2px -6px',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.grey[200]
    }
  },
  monoId: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
  },
  titleLink: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    borderRadius: 4,
    padding: '2px 6px',
    margin: '-2px -6px',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.grey[200]
    }
  }
}))

const MAX_TAGS: Record<TagReviewItem['entityType'], number> = {
  MarketplaceListing: 50,
  MessageSetMessage: 15,
  Pick: 15,
  Template: 15
}

function validateTag(tag: string): string | null {
  if (tag.length < 2) return 'Tags must be at least 2 characters'
  if (tag.length > 50) return 'Tags must be at most 50 characters'
  if (!/[a-zA-Z0-9]/.test(tag)) return 'Tags must contain at least one alphanumeric character'
  return null
}

const entityTypeLabels: Record<TagReviewItem['entityType'], string> = {
  MessageSetMessage: 'Message Set',
  MarketplaceListing: 'Marketplace Listing',
  Pick: 'Pick',
  Template: 'Template'
}

const REJECTION_BUCKETS = [
  { value: 'Irrelevant Tags', label: 'Irrelevant Tags' },
  { value: 'Lack of Breadth / Too Narrow', label: 'Lack of Breadth / Too Narrow' },
  { value: 'Other', label: 'Other' }
]

interface RejectionModalProps {
  open: boolean
  item: TagReviewItem
  loading: boolean
  onCancel: () => void
  onConfirm: (rejectionReason: string) => void
}

const RejectionModal: React.FC<RejectionModalProps> = ({ open, item, loading, onCancel, onConfirm }) => {
  const [selectedBucket, setSelectedBucket] = React.useState<string | null>(null)
  const [additionalContext, setAdditionalContext] = React.useState('')

  React.useEffect(() => {
    if (open) {
      setSelectedBucket(null)
      setAdditionalContext('')
    }
  }, [open])

  const handleConfirm = () => {
    if (!selectedBucket) return
    const ctx = additionalContext.trim()
    onConfirm(ctx ? `${selectedBucket}: ${ctx}` : selectedBucket)
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth='sm' fullWidth>
      <DialogTitle>Why are you rejecting this?</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='textSecondary' style={{ marginBottom: 12 }}>
          {getEntityIdLabel(item)}:{' '}
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
            {item.entityId}
          </span>
        </Typography>
        <Box style={{ backgroundColor: '#E3F2FD', borderRadius: 4, padding: '8px 12px', marginBottom: 20 }}>
          <Typography variant='caption' color='textSecondary'>
            Providing a specific reason helps our AI learn your preferences and reduces future manual reviews.
          </Typography>
        </Box>
        <Typography variant='caption' style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 8 }}>
          Rejection Reason
        </Typography>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {REJECTION_BUCKETS.map(bucket => (
            <Box
              key={bucket.value}
              onClick={() => setSelectedBucket(bucket.value)}
              style={{
                border: `2px solid ${selectedBucket === bucket.value ? '#1976d2' : '#e0e0e0'}`,
                borderRadius: 8,
                padding: '10px 16px',
                cursor: 'pointer',
                backgroundColor: selectedBucket === bucket.value ? '#E3F2FD' : 'transparent',
                transition: 'all 0.15s ease',
                userSelect: 'none'
              }}>
              <Typography variant='body2' style={{ fontWeight: selectedBucket === bucket.value ? 600 : 400 }}>
                {bucket.label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant='caption' style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>
          Additional Context{' '}
          <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(Optional)</span>
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          variant='outlined'
          size='small'
          placeholder='Add any specific details about why these tags were rejected...'
          value={additionalContext}
          onChange={e => setAdditionalContext(e.target.value)}
        />
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant='contained'
          disableElevation
          disabled={!selectedBucket || loading}
          onClick={handleConfirm}
          style={selectedBucket && !loading ? { backgroundColor: '#B71C1C', color: '#fff' } : undefined}
          startIcon={loading ? <CircularProgress size={16} color='inherit' /> : undefined}>
          {loading ? 'Rejecting…' : 'Confirm Rejection'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const statusConfig: Record<TagReviewStatus, { label: string; color: string }> = {
  pending: { label: 'Pending Review', color: '#FF9800' },
  queued: { label: 'Queued', color: '#2196F3' },
  processing: { label: 'Processing', color: '#9C27B0' },
  completed: { label: 'Complete', color: '#4CAF50' },
  error: { label: 'Error', color: '#F44336' },
  rejected: { label: 'Rejected', color: '#B71C1C' }
}

interface TagReviewCardProps {
  item: TagReviewItem
  status: TagReviewStatus
  onStatusChange: (id: string, newStatus: TagReviewStatus) => void
  onError?: (message: string) => void
}

const MessageCardContext: React.FC<{ item: TagReviewItem }> = ({ item }) => {
  const classes = useStyles()
  const entityPath = getEntityPath(item)
  const categories = item.categories
  const popularity = item.popularityScore

  return (
    <>
      <Box className={classes.titleRow}>
        {item.entityType === 'MessageSetMessage' && item.messageSetTitle ? (
          <Box display='flex' alignItems='center' justifyContent='space-between' style={{ width: '100%' }}>
            <Link
              variant='subtitle1'
              style={{ fontWeight: 500 }}
              color='textPrimary'
              className={classes.titleLink}
              onClick={() => HISTORY.push(`/message-sets/${item.messageSetId}`)}
              underline='hover'>
              {item.messageSetTitle}
              <OpenInNew style={{ fontSize: 14, opacity: 0.5 }} />
            </Link>
            {popularity != null && (
              <Tooltip
                title='Message set message popularity proxied by install count in the absence of send or like count'
                placement='top'>
                <Chip
                  icon={<GetApp style={{ fontSize: 14, color: '#4CAF50' }} />}
                  label={popularity.toLocaleString()}
                  size='small'
                  variant='outlined'
                  className={classes.popularityChip}
                />
              </Tooltip>
            )}
          </Box>
        ) : (
          <Box display='flex' alignItems='center' style={{ gap: 6 }}>
            <Typography variant='caption' color='textSecondary' style={{ fontWeight: 600 }}>
              Categories:
            </Typography>
            {categories && categories.length > 0 ? (
              <>
                {categories.slice(0, 3).map(cat => (
                  <Chip key={cat} label={cat} size='small' variant='outlined' className={classes.categoryChip} />
                ))}
                {categories.length > 3 && (
                  <Typography variant='caption' color='textSecondary'>
                    &hellip;
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant='caption' color='textSecondary'>
                None
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <Box display='flex' justifyContent='center' py={2} px={3} mb={8}>
        {item.characters ? (
          <MessageCard boardStyle='black' characters={item.characters as IBoard} />
        ) : (
          <Box
            display='flex'
            alignItems='center'
            justifyContent='center'
            style={{ width: '100%', aspectRatio: '22/6', backgroundColor: '#111', borderRadius: 4 }}>
            <Typography variant='caption' style={{ color: '#555' }}>
              No characters for review item.
            </Typography>
          </Box>
        )}
      </Box>
      <Box className={classes.footerWrapper}>
        <Box className={classes.footer}>
          <Link
            variant='caption'
            color='textSecondary'
            className={classes.entityLink}
            onClick={() => HISTORY.push(entityPath)}
            underline='hover'>
            {getEntityIdLabel(item)}: <span className={classes.monoId}>{truncateId(item.entityId)}</span>
          </Link>
          {popularity != null && item.entityType !== 'MessageSetMessage' && (
            <Chip
              icon={<Favorite style={{ fontSize: 14, color: '#D92C26' }} />}
              label={popularity.toLocaleString()}
              size='small'
              variant='outlined'
              className={classes.popularityChip}
            />
          )}
        </Box>
      </Box>
    </>
  )
}

const MarketplaceListingContext: React.FC<{ item: TagReviewItem }> = ({ item }) => {
  const classes = useStyles()
  const title = item.listingTitle
  const description = item.listingDescription
  const iconUrl = item.listingIconUrl
  const popularity = item.popularityScore
  const entityPath = getEntityPath(item)

  return (
    <>
      <Box className={classes.mplContent}>
        <Avatar src={iconUrl ?? undefined} variant='rounded' className={classes.mplIcon} />
        <Box>
          <Link
            variant='subtitle1'
            style={{ fontWeight: 700, lineHeight: 1.2, marginBottom: 4, display: 'block' }}
            color='textPrimary'
            className={classes.entityLink}
            onClick={() => HISTORY.push(entityPath)}
            underline='hover'>
            {title}
          </Link>
          {description && (
            <Typography
              variant='body2'
              color='textSecondary'
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
              {description}
            </Typography>
          )}
        </Box>
      </Box>
      <Box className={classes.footerWrapper}>
        <Box className={classes.footer}>
          <Link
            variant='caption'
            color='textSecondary'
            className={classes.entityLink}
            onClick={() => HISTORY.push(entityPath)}
            underline='hover'>
            {getEntityIdLabel(item)}: <span className={classes.monoId}>{truncateId(item.entityId)}</span>
          </Link>
          {popularity !== undefined && (
            <Chip
              icon={<GetApp style={{ fontSize: 14, color: '#4CAF50' }} />}
              label={popularity?.toLocaleString()}
              size='small'
              variant='outlined'
              className={classes.popularityChip}
            />
          )}
        </Box>
      </Box>
    </>
  )
}

interface TagReviewControlsProps {
  item: TagReviewItem
  status: TagReviewStatus
  onStatusChange: (id: string, newStatus: TagReviewStatus) => void
  onError?: (message: string) => void
}

const TagReviewControls: React.FC<TagReviewControlsProps> = ({ item, status, onStatusChange, onError }) => {
  const classes = useStyles()
  const theme = useTheme()
  const initialAiTags = React.useRef(item.suggestedTags).current
  const [localStatus, setLocalStatus] = React.useState(status)
  const [isEditingQueued, setIsEditingQueued] = React.useState(false)
  const initialTags = item.acceptedTags ?? item.suggestedTags
  const [tags, setTags] = React.useState<string[]>(initialTags)
  const [tagError, setTagError] = React.useState<string | null>(null)
  const [actionLoading, setActionLoading] = React.useState(false)
  const [rejectionModalOpen, setRejectionModalOpen] = React.useState(false)
  const statusCfg = statusConfig[localStatus]
  const maxTags = MAX_TAGS[item.entityType]

  const [approveItem] = useApproveTagReviewItem()
  const [rejectItem] = useRejectTagReviewItem()
  const [revertItem] = useRevertTagReviewItem()
  const [unRejectItem] = useUnRejectTagReviewItem()
  const [updateTags] = useUpdateTagReviewItemTags()
  const [retryItem] = useRetryTagReviewItem()

  React.useEffect(() => {
    setLocalStatus(status)
  }, [status])

  const isQueued = localStatus === 'queued'
  const isError = localStatus === 'error'
  const isEditable = localStatus === 'pending' || (isQueued && isEditingQueued)

  const getError = (data: any, key: string): string | null => {
    const result = data?.[key]
    if (!result?.__typename?.includes('Error')) return null
    return result.errorMessage || 'An unknown error occurred'
  }

  const handleApprove = async () => {
    setActionLoading(true)
    try {
      const approveRes = await approveItem({ variables: { input: { id: item.id, batchId: item.batchId, acceptedTags: tags } } })
      const approveErr = getError(approveRes.data, 'approveTagReviewItemV2')
      if (approveErr) { onError?.(approveErr); return }

      setLocalStatus('queued')
      onStatusChange(item.id, 'queued')
    } catch {
      onError?.('Failed to approve item')
    } finally {
      setActionLoading(false)
    }
  }

  const handleReject = () => {
    setRejectionModalOpen(true)
  }

  const handleConfirmRejection = async (rejectionReason: string) => {
    setActionLoading(true)
    try {
      const res = await rejectItem({ variables: { input: { id: item.id, batchId: item.batchId, rejectionReason } } })
      const err = getError(res.data, 'rejectTagReviewItemV2')
      if (err) { onError?.(err); return }
      setRejectionModalOpen(false)
      setLocalStatus('rejected')
      onStatusChange(item.id, 'rejected')
    } catch {
      onError?.('Failed to reject item')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCancelQueue = async () => {
    setActionLoading(true)
    try {
      const res = await revertItem({ variables: { input: { id: item.id, batchId: item.batchId } } })
      const err = getError(res.data, 'revertTagReviewItemV2')
      if (err) { onError?.(err); return }
      setLocalStatus('pending')
      onStatusChange(item.id, 'pending')
      setTags(initialTags)
      setTagError(null)
    } catch {
      onError?.('Failed to revert item')
    } finally {
      setActionLoading(false)
    }
  }

  const handleUpdateTags = async () => {
    setActionLoading(true)
    try {
      const res = await updateTags({ variables: { input: { id: item.id, batchId: item.batchId, acceptedTags: tags } } })
      const err = getError(res.data, 'updateTagReviewItemTagsV2')
      if (err) { onError?.(err); return }
      setIsEditingQueued(false)
    } catch {
      onError?.('Failed to update tags')
    } finally {
      setActionLoading(false)
    }
  }

  const handleUnReject = async () => {
    setActionLoading(true)
    try {
      const res = await unRejectItem({ variables: { input: { id: item.id, batchId: item.batchId } } })
      const err = getError(res.data, 'unRejectTagReviewItemV2')
      if (err) { onError?.(err); return }
      setLocalStatus('pending')
      onStatusChange(item.id, 'pending')
    } catch {
      onError?.('Failed to un-reject item')
    } finally {
      setActionLoading(false)
    }
  }

  const handleRetry = async () => {
    setActionLoading(true)
    try {
      const res = await retryItem({ variables: { input: { id: item.id, batchId: item.batchId } } })
      const err = getError(res.data, 'retryTagReviewItemV2')
      if (err) { onError?.(err); return }
      setLocalStatus('pending')
      onStatusChange(item.id, 'pending')
    } catch {
      onError?.('Failed to retry item')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCancelEdit = () => {
    setTags(initialTags)
    setIsEditingQueued(false)
    setTagError(null)
  }

  const hasAcceptedTags = item.acceptedTags !== null && item.acceptedTags !== undefined
  const restoreLabel = hasAcceptedTags && tags.join(',') !== item.acceptedTags!.join(',')
    ? 'Restore Saved Tags'
    : 'Restore AI Suggestions'

  const handleRestoreTags = () => {
    setTagError(null)
    if (hasAcceptedTags && tags.join(',') !== item.acceptedTags!.join(',')) {
      setTags([...item.acceptedTags!])
    } else {
      setTags([...item.suggestedTags])
    }
  }

  return (
    <>
      <Box className={classes.controlsHeader}>
        <Chip
          icon={getEntityIcon(item.entityType)}
          label={entityTypeLabels[item.entityType]}
          size='small'
          variant='outlined'
          className={classes.popularityChip}
        />
        <Box display='flex' flexDirection='column' alignItems='flex-end'>
          <Box display='flex' alignItems='center' style={{ gap: 6 }}>
            <Box
              component='span'
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: statusCfg.color,
                display: 'inline-block'
              }}
            />
            <Typography variant='caption' style={{ fontWeight: 600, color: statusCfg.color }}>
              {statusCfg.label}
            </Typography>
          </Box>
          {isQueued && item.queuedAt && (
            <Typography variant='caption' color='textSecondary' style={{ fontSize: '0.65rem' }}>
              Queued:{' '}
              {new Date(item.queuedAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
          )}
        </Box>
      </Box>

      {localStatus === 'rejected' && item.rejectionReason && (
        <Box style={{ backgroundColor: '#FFF3E0', borderTop: '1px solid #FFE0B2', borderBottom: '1px solid #FFE0B2', padding: '10px 24px' }}>
          <Typography variant='caption' style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#E65100', display: 'block', marginBottom: 2 }}>
            Rejection Reason
          </Typography>
          <Typography variant='caption' style={{ color: '#BF360C' }}>
            {item.rejectionReason}
          </Typography>
        </Box>
      )}

      <Box className={classes.controlsBody}>
        <Box display='flex' justifyContent='space-between' alignItems='center' style={{ marginBottom: 8 }}>
          <Box display='flex' alignItems='center' style={{ gap: 8 }}>
            <Typography
              variant='caption'
              color='textSecondary'
              style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Tags
            </Typography>
            <Typography
              variant='caption'
              style={{
                fontWeight: 700,
                color: tags.length < 5 ? theme.palette.text.disabled : tags.length <= maxTags ? '#4CAF50' : '#FF9800'
              }}>
              {tags.length}/{maxTags}
            </Typography>
            {item.suggestedTags.length > 0 && (
              <Button
                size='small'
                color='primary'
                disabled={!isEditable}
                onClick={handleRestoreTags}
                style={{ minWidth: 'auto', padding: '0 8px', fontSize: '0.7rem', textTransform: 'none' }}>
                {restoreLabel}
              </Button>
            )}
          </Box>
          <Box display='flex' alignItems='center' style={{ gap: 12 }}>
            <Box display='flex' alignItems='center' style={{ gap: 4 }}>
              <Box
                component='span'
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  display: 'inline-block'
                }}
              />
              <Typography variant='caption' color='textSecondary' style={{ fontSize: '0.65rem' }}>
                AI Suggested
              </Typography>
            </Box>
            <Box display='flex' alignItems='center' style={{ gap: 4 }}>
              <Box
                component='span'
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.grey[500],
                  display: 'inline-block'
                }}
              />
              <Typography variant='caption' color='textSecondary' style={{ fontSize: '0.65rem' }}>
                User Added
              </Typography>
            </Box>
          </Box>
        </Box>

        <Autocomplete
          multiple
          freeSolo
          size='small'
          disabled={!isEditable}
          options={[] as string[]}
          value={tags}
          onChange={(_: any, newValue: any) => {
            const incoming = newValue as string[]
            const added = incoming.filter(t => !tags.includes(t))
            for (const tag of added) {
              const err = validateTag(tag)
              if (err) {
                setTagError(err)
                return
              }
            }
            setTagError(null)
            const deduped = Array.from(new Set(incoming))
            setTags(deduped)
          }}
          className={classes.tagAutocomplete}
          renderTags={(value: any[], getTagProps: any) =>
            value.map((option: string, index: number) => {
              const isAi = initialAiTags.includes(option)
              return (
                <Chip
                  key={index}
                  label={option}
                  size='small'
                  variant='outlined'
                  color={isAi ? 'primary' : 'default'}
                  {...getTagProps({ index })}
                  style={{ fontWeight: 500 }}
                />
              )
            })
          }
          renderInput={(params: any) => (
            <TextField
              {...params}
              placeholder={tags.length === 0 ? `Type a tag (aim for 5–${maxTags} across different categories)...` : 'Add another tag...'}
              variant='outlined'
              error={tags.length > maxTags || Boolean(tagError)}
            />
          )}
        />
        {tags.length > maxTags && (
          <Typography variant='caption' style={{ color: '#F44336', marginTop: 4 }}>
            Too many tags hurts search ranking. Try removing synonyms.
          </Typography>
        )}
        {tagError && (
          <Typography variant='caption' style={{ color: '#F44336', marginTop: 4 }}>
            {tagError}
          </Typography>
        )}
      </Box>

      {isError && (
        <Box style={{ backgroundColor: '#FFEBEE', borderTop: '1px solid #FFCDD2', padding: '10px 24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <Box>
            <Typography variant='caption' style={{ fontWeight: 700, color: '#B71C1C', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
              {item.error?.type ?? 'Unknown Error'}
            </Typography>
            <Typography variant='caption' style={{ color: '#C62828' }}>
              {item.error?.message ?? 'An error occurred while processing this item.'}
            </Typography>
          </Box>
          <Tooltip title='Returns this item to pending so it can be re-reviewed and re-queued' placement='top'>
            <span>
              <Button size='small' variant='outlined' disabled={actionLoading} onClick={handleRetry}
                style={{ color: '#B71C1C', borderColor: '#B71C1C', flexShrink: 0, whiteSpace: 'nowrap' }}>
                Reopen
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      <RejectionModal
        open={rejectionModalOpen}
        item={item}
        loading={actionLoading}
        onCancel={() => setRejectionModalOpen(false)}
        onConfirm={handleConfirmRejection}
      />

      {!isError && <Box className={classes.controlsFooter}>
        {localStatus === 'pending' && (
          <>
            <Button
              variant='outlined'
              disableElevation
              disabled={actionLoading}
              onClick={handleReject}
              style={{ color: '#B71C1C', borderColor: '#B71C1C' }}>
              Reject
            </Button>
            <Box style={{ width: 8 }} />
            <Button variant='contained' disableElevation color='primary' disabled={actionLoading || tags.length < 5 || tags.length > maxTags} onClick={handleApprove}>
              Approve &amp; Queue
            </Button>
          </>
        )}
        {isQueued &&
          (isEditingQueued ? (
            <>
              <Button variant='outlined' disableElevation disabled={actionLoading} onClick={handleCancelEdit}>
                Cancel Edit
              </Button>
              <Box style={{ width: 8 }} />
              <Button variant='contained' disableElevation color='primary' disabled={actionLoading} onClick={handleUpdateTags}>
                Update Tags
              </Button>
            </>
          ) : (
            <>
              <Button variant='outlined' disableElevation color='secondary' disabled={actionLoading} onClick={handleCancelQueue}>
                Revert to Pending
              </Button>
              <Box style={{ width: 8 }} />
              <Button variant='outlined' disableElevation color='primary' disabled={actionLoading} onClick={() => setIsEditingQueued(true)}>
                Edit Tags
              </Button>
            </>
          ))}
        {localStatus === 'processing' && (
          <Typography variant='caption' color='textSecondary'>
            Processing...
          </Typography>
        )}
        {localStatus === 'completed' && (
          <Typography variant='caption' style={{ color: statusConfig.completed.color, fontWeight: 600 }}>
            Tags applied
          </Typography>
        )}
        {localStatus === 'rejected' && (
          <Button variant='outlined' disableElevation disabled={actionLoading} onClick={handleUnReject}
            style={{ color: '#B71C1C', borderColor: '#B71C1C' }}>
            Undo Rejection
          </Button>
        )}
      </Box>}
    </>
  )
}

export const TagReviewCard: React.FC<TagReviewCardProps> = ({ item, status, onStatusChange, onError }) => {
  const classes = useStyles()
  const isMessageCard = item.entityType !== 'MarketplaceListing'

  return (
    <Paper className={classes.card} variant='outlined'>
      <Box className={`${classes.leftColumn} ${isMessageCard ? classes.leftColumnMessageCard : classes.leftColumnMpl}`}>
        {isMessageCard ? <MessageCardContext item={item} /> : <MarketplaceListingContext item={item} />}
      </Box>
      <Box className={classes.rightColumn}>
        <TagReviewControls item={item} status={status} onStatusChange={onStatusChange} onError={onError} />
      </Box>
    </Paper>
  )
}
